import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  Navbar,
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  PricingSection,
  CTASection,
  Footer
} from "@/components/landing";
import MarketingPage from "@/components/marketing/MarketingPage";

// Use current origin for redirects in production, fallback to env variable for development
const BACKEND_URL = window.location.hostname === 'localhost' 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin;

/**
 * Custom hook for theme management
 */
const useTheme = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Initialize theme from localStorage or system preference
    const savedTheme = localStorage.getItem('tutrlink:theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDark(savedTheme === 'dark' || (!savedTheme && prefersDark));
  }, []); // Empty deps - runs once on mount

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const newTheme = !prev;
      localStorage.setItem('tutrlink:theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  return { isDark, toggleTheme };
};

/**
 * Landing Page Component - Main marketing page
 */
const LandingPage = () => {
  const { isDark, toggleTheme } = useTheme();

  const scrollToSection = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className={`landing-page ${isDark ? 'dark' : ''}`} data-testid="landing-page">
      <Navbar 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        scrollToSection={scrollToSection} 
      />
      <HeroSection scrollToSection={scrollToSection} />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <CTASection />
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
};

/**
 * Process OAuth authentication callback
 */
const processOAuthCallback = async (location, navigate, setError) => {
  try {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');
    const searchParams = new URLSearchParams(location.search);
    const userType = searchParams.get('type') || 'tutor';

    if (!sessionId) {
      throw new Error('No session ID found');
    }

    const response = await fetch(`${BACKEND_URL}/api/auth/google/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, user_type: userType }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Authentication failed');
    }

    // Redirect based on user type
    const redirectUrl = userType === 'parent' 
      ? `${BACKEND_URL}/api/parent/`
      : `${BACKEND_URL}/api/app/`;
    
    window.location.href = redirectUrl;
  } catch (err) {
    console.error('Auth error:', err);
    setError(err.message);
    setTimeout(() => navigate('/'), 3000);
  }
};

/**
 * Auth Callback Component for Google OAuth
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    processOAuthCallback(location, navigate, setError);
  }, [navigate, location]);

  if (error) {
    return (
      <div className="auth-callback" data-testid="auth-error">
        <div className="auth-card error">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <p className="redirect-msg">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback" data-testid="auth-loading">
      <div className="auth-card">
        <div className="spinner"></div>
        <p>Completing sign in...</p>
      </div>
    </div>
  );
};

/**
 * Main App Component
 */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MarketingPage file="home.html" />} />
          <Route path="/home" element={<MarketingPage file="home.html" />} />
          <Route path="/how-it-works" element={<MarketingPage file="how-it-works.html" />} />
          <Route path="/pricing" element={<MarketingPage file="pricing.html" />} />
          <Route path="/signup" element={<MarketingPage file="signup.html" />} />
          <Route path="/index" element={<MarketingPage file="index.html" />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
